"use client";

import { useEffect } from "react";
import ReactDOM from "react-dom";
import { keyframes, styled } from "styled-components";
/**
 * 주석 이름 / Section: Modal (공통 모달 컴포넌트)
 * 코드 목적 / Purpose: 다양한 상황(알림, 확인, 폼 등)에 재사용 가능한 범용 모달 UI를 제공합니다.
 * 산출물 / Output: header/body/footer 구조의 중앙 고정 모달, showCloseButton 등 옵션 지원 (optional)
 *
 * 시나리오(알고리즘) / Scenario (Algorithm Flow): (optional)
 *   1. isOpen이 true일 때만 Portal로 모달을 렌더링합니다.
 *   2. Overlay 클릭 시 onClose 호출(외부 닫힘 허용), 내부 콘텐츠 클릭은 이벤트 전파 방지 필요.
 *   3. header/body/footer props로 다양한 UI를 커스터마이즈할 수 있습니다.
 *
 * 관련 모듈/클래스 / Related Modules/Classes: BtnElement, AppColors, AppTextStyles 등 (optional)
 * 입력 값 / Input Values: isOpen, onClose, showCloseButton, header, body, footer, 스타일 props 등 (optional)
 * 예외 처리 / Exception Handling: isOpen=false 또는 window 미존재 시 렌더링하지 않음 (optional)
 */




const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div<{ $zIndex?: number }>`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${({ $zIndex }) => ($zIndex ? $zIndex - 1 : 9997)};
  animation: ${fadeIn} 0.2s ease-out;
  pointer-events: auto;
`;

type ModalProps = {
  $bgColor?: string;
  $padding?: string;
  $borderRadius?: string;
  $width?: string;
  $height?: string;
  $zIndex?: number;
};

const ModalWrapper = styled.div<ModalProps>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  background-color: ${({ $bgColor }) => $bgColor || "#FFFFFF"};
  padding: ${({ $padding }) => $padding || "24px 14px 12px 14px"};
  border-radius: ${({ $borderRadius }) => $borderRadius || "14px"};
  width: ${({ $width }) => $width || "300px"};
  max-width: calc(100vw - 32px); // 좌우 16px씩 여백 확보
  min-width: 280px; // 최소 너비 설정
  // height: ${({ $height }) => $height || "186px"};
  z-index: ${({ $zIndex }) => $zIndex || 9998};

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ModalHeader = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 760;
  color: #000000;
  margin-bottom: 16px; // Space between header and body
  white-space: pre-line; // Respect newlines in header content
`;

const ModalBody = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: #000000;
  text-align: center;
  margin-bottom: 24px; // Space before footer
  flex-grow: 1; // Allow body to take available space
  white-space: pre-line; // Respect newlines in body content
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
`;

type ModalComponentProps = {
  isOpen: boolean;
  onClose?: () => void;
  bgColor?: string;
  padding?: string;
  borderRadius?: string;
  width?: string;
  height?: string;
  zIndex?: number;

  header?: string;
  body?: React.ReactNode;
  footer?: React.ReactNode;
};

export default function Modal({
  isOpen,
  onClose,
  bgColor,
  padding,
  borderRadius,
  width,
  height,
  zIndex,
  header,
  body,
  footer,
}: ModalComponentProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      
      // ESC 키로 모달 닫기 (이벤트 전파 방지)
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.stopPropagation(); // 상위 컴포넌트로 이벤트 전파 방지
          if (onClose) {
            onClose();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown, true); // capture phase에서 처리
      
      return () => {
        document.body.style.overflow = "";
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [isOpen, onClose]);

  const modal = (
    <Overlay onClick={onClose} $zIndex={zIndex}>
      <ModalWrapper
        $bgColor={bgColor}
        $padding={padding}
        $borderRadius={borderRadius}
        $width={width}
        $height={height}
        $zIndex={zIndex}
        onClick={(e) => e.stopPropagation()} // Prevent overlay close when clicking inside modal
      >
        {header && <ModalHeader>{header}</ModalHeader>}
        <ModalBody>{body}</ModalBody>
        <ModalFooter>{footer}</ModalFooter>
      </ModalWrapper>
    </Overlay>
  );

  if (!isOpen || typeof window === "undefined") return null;

  return ReactDOM.createPortal(modal, document.body);
}
